'use client';

import React, { useState, useEffect, Suspense } from 'react';
import { useSearchParams } from 'next/navigation';
import { useLanguageVariables } from '@/contexts/LanguageVariablesContext';
import { CheckCircleIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

export interface VerifyBrochureEmailPageProps {
  language?: string;
  translations?: Record<string, string>;
}

function VerifyBrochureEmailContent() {
  const searchParams = useSearchParams();
  const { getVariable } = useLanguageVariables();
  const t = (key: string, fallback: string) => getVariable(key, fallback) ?? fallback;

  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState<string | null>(null);

  const token = searchParams?.get('token') ?? null;
  const email = searchParams?.get('email') ?? null;

  useEffect(() => {
    if (!token || !email) {
      setStatus('error');
      setMessage(getVariable('verify_brochure_email.missing_params', 'Invalid link. Token and email are required.') ?? 'Invalid link. Token and email are required.');
      return;
    }

    setStatus('loading');
    setMessage(getVariable('verify_brochure_email.verifying', 'Verifying your email...') ?? 'Verifying your email...');

    const url = `/api/brochure-download/verify-email?${new URLSearchParams({ token, email }).toString()}`;
    fetch(url, { method: 'GET', headers: { Accept: 'application/json' } })
      .then((res) => res.json())
      .then((data) => {
        if (data?.success) {
          setStatus('success');
          setMessage(getVariable('verify_brochure_email.success', 'Your email has been verified. The brochure download link has been sent to your email.') ?? 'Your email has been verified. The brochure download link has been sent to your email.');
        } else {
          setStatus('error');
          setMessage(data?.message ?? (getVariable('verify_brochure_email.invalid', 'This link is invalid or has expired.') ?? 'This link is invalid or has expired.'));
        }
      })
      .catch(() => {
        setStatus('error');
        setMessage(getVariable('verify_brochure_email.error', 'Something went wrong. Please try again later.') ?? 'Something went wrong. Please try again later.');
      });
  }, [token, email, getVariable]);

  return (
    <div className="min-h-[60vh] flex items-center justify-center px-4 py-12">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-neutral-200/90 p-8 text-center">
        {status === 'loading' && (
          <>
            <div className="inline-flex w-16 h-16 rounded-full bg-alsero-first/10 items-center justify-center mb-4">
              <div className="w-8 h-8 border-2 border-alsero-first border-t-transparent rounded-full animate-spin" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              {t('verify_brochure_email.title', 'Verify your email')}
            </h1>
            <p className="text-gray-600">{message}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="inline-flex w-16 h-16 rounded-full bg-green-100 items-center justify-center mb-4">
              <CheckCircleIcon className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              {t('verify_brochure_email.success_title', 'Email verified')}
            </h1>
            <p className="text-gray-600">{message}</p>
          </>
        )}
        {status === 'error' && (
          <>
            <div className="inline-flex w-16 h-16 rounded-full bg-amber-100 items-center justify-center mb-4">
              <ExclamationTriangleIcon className="w-10 h-10 text-amber-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 mb-2">
              {t('verify_brochure_email.error_title', 'Verification failed')}
            </h1>
            <p className="text-gray-600">{message}</p>
          </>
        )}
      </div>
    </div>
  );
}

export default function VerifyBrochureEmailPage({ language, translations }: VerifyBrochureEmailPageProps) {
  return (
    <Suspense
      fallback={
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-alsero-first border-t-transparent rounded-full animate-spin" />
        </div>
      }
    >
      <VerifyBrochureEmailContent />
    </Suspense>
  );
}
